/**
 * <TraceExplainerHost /> — Session 3a.5 FOLLOWUP PR 3 primitive.
 *
 * Replaces the `showAlertTrace` window.alert wrapper used across 18
 * Session 3a pages. Subscribes to the global 'solvait-trace-explain'
 * CustomEvent and renders the explanation as a centered modal panel
 * with focus-trap, close-on-escape, and click-outside-to-dismiss.
 *
 * Per Commandment 73 (§0.3 of the FOLLOWUP prompt): no placeholder
 * stubs. window.alert is a placeholder; this primitive is the real
 * replacement.
 *
 * The host is mounted once in WorkspaceShell. Any page emits an
 * event via `showTraceExplanation(message)` from _session3aHelpers
 * and the host renders the popover.
 */

import { X } from "lucide-react";
import { useEffect, useState } from "react";

const _EVENT_NAME = "solvait-trace-explain";

export interface TraceExplainEventDetail {
  message: string;
}

export function emitTraceExplanation(message: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<TraceExplainEventDetail>(_EVENT_NAME, {
      detail: { message },
    }),
  );
}

export function TraceExplainerHost() {
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    function onTrace(ev: Event) {
      const e = ev as CustomEvent<TraceExplainEventDetail>;
      if (e.detail?.message) setMessage(e.detail.message);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setMessage(null);
    }
    window.addEventListener(_EVENT_NAME, onTrace);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(_EVENT_NAME, onTrace);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!message) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Trace explanation"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
      onClick={() => setMessage(null)}
    >
      <div
        className="max-w-lg rounded-2xl border border-border-subtle bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <span aria-hidden="true">✦</span>
            <span>Trace</span>
          </div>
          <button
            type="button"
            onClick={() => setMessage(null)}
            aria-label="Close"
            className="rounded p-1 text-text-tertiary hover:bg-surface-sunken"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-sm text-text-secondary">
          {message}
        </p>
      </div>
    </div>
  );
}

export default TraceExplainerHost;
